import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { LanguageService } from '../../../services/i18n/language';
import { CustomsCalculationsService, CustomsCalculation } from '../../../services/api/customs-calculations';
import { CargoRequest } from '../../../services/api/cargo-request';
import { AuthService } from '../../../services/auth/auth';
import { ViewCalculationDetailsComponent } from '../../shared/customs-calculations/view-calculation-details/view-calculation-details';
import { UpgradeToCargoOwnerModalComponent } from '../upgrade-to-cargo-owner-modal/upgrade-to-cargo-owner-modal';
import { CreateRequestComponent } from '../../cargo-owner/create-request/create-request';
import { ViewRequestComponent } from '../../cargo-owner/view-request/view-request';
import { ViewDealComponent } from '../../shared/view-deal/view-deal';
import { SupplierDocumentComponent } from '../../shared/supplier-document/supplier-document';
import { ContractUploadComponent } from '../../shared/contract-upload/contract-upload';
import { Subscription } from 'rxjs';
import { CALC_CABINET_ROLES } from '../../shared/customs-calculations/calculation-sections';

/**
 * Страница обработки расчёта («Обработать» на /calculations/:id): две вкладки —
 * предпросмотр самого расчёта и оформление перевозки (создание заявки → просмотр заявки →
 * просмотр сделки, как только ставка экспедитора принята — всё в рамках этой же вкладки).
 */
@Component({
  selector: 'app-process-calculation',
  standalone: true,
  imports: [
    CommonModule, RouterModule, ViewCalculationDetailsComponent,
    UpgradeToCargoOwnerModalComponent, CreateRequestComponent, ViewRequestComponent, ViewDealComponent,
    SupplierDocumentComponent, ContractUploadComponent
  ],
  templateUrl: './process-calculation.html',
  styleUrl: './process-calculation.scss'
})
export class ProcessCalculationComponent implements OnInit, OnDestroy {
  /** Встроен во вкладку страницы поставки: только содержимое раздела, без шапки и переходов. */
  @Input() embeddedCalculationId?: number;
  @Input() embeddedSection?: 'supply' | 'transportation';

  calculationId!: number;
  calculation: CustomsCalculation | null = null;
  loading = true;
  error: string | null = null;

  /** Заявки, созданные из этого расчёта — их может быть несколько (сравнение стоимости
   *  перевозки по разным заявкам); сделка в итоге только по одной из них. */
  cargoRequests: CargoRequest[] = [];
  /** ID заявок, у которых карточка сейчас развёрнута (по умолчанию — все). */
  expandedRequestIds = new Set<number>();
  /** Показывать форму создания ещё одной заявки на этот же расчёт. */
  showAddRequestForm = false;

  activeTab: 'calculation' | 'transportation' | 'supply' = 'calculation';
  /** Раздел кабинета из маршрута (/supplier, /transport); null — прежний адрес /process. */
  cabinetSection: 'supply' | 'transportation' | null = null;
  showUpgradeModal = false;

  /** Вкладка «Поставка» — ссылка-приглашение поставщика, пока он не привязан к расчёту. */
  supplierInviteLink: string | null = null;
  generatingSupplierInvite = false;
  supplierInviteError: string | null = null;
  supplierInviteCopied = false;

  backText = 'К расчёту';
  titleText = 'Обработка расчёта {title}';
  loadingText = 'Загрузка...';
  tabCalculationText = 'Предварительный расчёт';
  tabTransportationText = 'Перевозка';
  tabSupplyText = 'Поставщик';
  needCompanyDataHintText = 'Чтобы оформить перевозку, заполните данные компании.';
  supplyOwnerOnlyHintText = 'Раздел «Поставка» доступен только грузовладельцу.';
  supplierConnectedText = 'Поставщик подключён:';
  supplierInviteHintText = 'К этой перевозке пока не привязан поставщик. Сгенерируйте ссылку-приглашение и отправьте её поставщику любым удобным способом (WhatsApp, email и т.п.).';
  generatingInviteText = 'Создаём ссылку...';
  invitesSupplierButtonText = 'Пригласить поставщика';
  copiedText = 'Скопировано ✓';
  copyText = 'Скопировать';
  errorLoadCalculationText = 'Ошибка при загрузке расчёта';
  errorGenerateInviteText = 'Ошибка при создании ссылки-приглашения';
  addRequestButtonText = '+ Добавить запрос';
  addRequestHintText = 'Можно создать ещё одну заявку на этот же расчёт — например, с другими условиями перевозки, чтобы сравнить стоимость. Сделка в итоге будет заключена только по одной из них.';
  collapseRequestText = 'Свернуть';
  expandRequestText = 'Развернуть';
  requestFallbackPrefixText = 'Заявка #';
  bidsCountSuffixText = 'ставок';

  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private languageService: LanguageService,
    private customsCalculationsService: CustomsCalculationsService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;

    this.updateTranslations();
    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.markForCheck();
      })
    );

    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/login']);
      return;
    }

    if (this.embeddedCalculationId) {
      this.calculationId = this.embeddedCalculationId;
      this.activeTab = this.embeddedSection ?? 'transportation';
      this.load(false);
      return;
    }

    // Подписываемся на paramMap, а не берём снимок один раз: при переходе между
    // разными /calculations/:id/process Angular переиспользует этот же экземпляр
    // компонента (тот же класс маршрута), ngOnInit повторно не вызывается —
    // без подписки calculationId "залипал" бы на первом открытом расчёте.
    this.subscriptions.add(
      this.route.paramMap.subscribe(params => {
        const id = params.get('id');
        if (!id) {
          this.router.navigate(['/']);
          return;
        }
        this.calculationId = +id;
        this.calculation = null;
        this.cargoRequests = [];
        this.expandedRequestIds.clear();
        this.showAddRequestForm = false;
        this.cabinetSection = this.route.snapshot.data['processSection'] ?? null;
        // В кабинете вкладок нет: раздел выбирается в боковом меню; /process — «Перевозка»
        this.activeTab = this.inCabinet ? (this.cabinetSection ?? 'transportation') : 'calculation';
        this.supplierInviteLink = null;
        this.supplierInviteError = null;
        this.supplierInviteCopied = false;
        // Форма «стать грузовладельцем» нужна только публичному пользователю,
        // который до этого считал расчёт анонимно/без роли — брокеру, экспедитору
        // и уже действующему грузовладельцу она показываться не должна.
        this.showUpgradeModal = this.isPublicUser();
        // Обработку запускаем только с прежнего адреса /process («Продолжить оформление»), не при открытии разделов
        this.load(!this.cabinetSection);
      })
    );
  }

  t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return !value || value === key ? fallback : value;
  }

  private updateTranslations(): void {
    this.backText = this.t('public.processCalculation.back', 'К расчёту');
    this.titleText = this.t('public.processCalculation.title', 'Обработка расчёта {title}');
    this.loadingText = this.t('common.loading', 'Загрузка...');
    this.tabCalculationText = this.t('public.processCalculation.tabCalculation', 'Предварительный расчёт');
    this.tabTransportationText = this.t('public.processCalculation.tabTransportation', 'Перевозка');
    this.tabSupplyText = this.t('public.processCalculation.tabSupply', 'Поставщик');
    this.needCompanyDataHintText = this.t('public.processCalculation.needCompanyDataHint', 'Чтобы оформить перевозку, заполните данные компании.');
    this.supplyOwnerOnlyHintText = this.t('public.processCalculation.supplyOwnerOnlyHint', 'Раздел «Поставка» доступен только грузовладельцу.');
    this.supplierConnectedText = this.t('public.processCalculation.supplierConnected', 'Поставщик подключён:');
    this.supplierInviteHintText = this.t('public.processCalculation.supplierInviteHint', 'К этой перевозке пока не привязан поставщик. Сгенерируйте ссылку-приглашение и отправьте её поставщику любым удобным способом (WhatsApp, email и т.п.).');
    this.generatingInviteText = this.t('public.processCalculation.generatingInvite', 'Создаём ссылку...');
    this.invitesSupplierButtonText = this.t('public.processCalculation.inviteSupplierButton', 'Пригласить поставщика');
    this.copiedText = this.t('public.processCalculation.copied', 'Скопировано ✓');
    this.copyText = this.t('public.processCalculation.copy', 'Скопировать');
    this.errorLoadCalculationText = this.t('public.processCalculation.errorLoadCalculation', 'Ошибка при загрузке расчёта');
    this.errorGenerateInviteText = this.t('public.processCalculation.errorGenerateInvite', 'Ошибка при создании ссылки-приглашения');
    this.addRequestButtonText = this.t('public.processCalculation.addRequestButton', '+ Добавить запрос');
    this.addRequestHintText = this.t('public.processCalculation.addRequestHint', 'Можно создать ещё одну заявку на этот же расчёт — например, с другими условиями перевозки, чтобы сравнить стоимость. Сделка в итоге будет заключена только по одной из них.');
    this.collapseRequestText = this.t('public.processCalculation.collapseRequest', 'Свернуть');
    this.expandRequestText = this.t('public.processCalculation.expandRequest', 'Развернуть');
    this.requestFallbackPrefixText = this.t('public.processCalculation.requestFallbackPrefix', 'Заявка #');
    this.bidsCountSuffixText = this.t('public.processCalculation.bidsCountSuffix', 'ставок');
  }

  /** Клиент (грузовладелец, новый пользователь, админ) — страница в кабинете расчёта; брокер — прежняя с вкладками. */
  get inCabinet(): boolean {
    return CALC_CABINET_ROLES.includes(this.authService.getUser()?.role?.code ?? '');
  }

  /** Заголовок раздела в кабинете. */
  get sectionTitle(): string {
    return this.activeTab === 'supply' ? this.tabSupplyText : this.tabTransportationText;
  }

  get embedded(): boolean {
    return !!this.embeddedCalculationId;
  }

  /** Грузовладелец оформляет поставку на её странице: со старых адресов (после запуска обработки) — туда. */
  private redirectToSection(): void {
    if (!this.embedded && this.isCargoOwner()) {
      const tab = this.cabinetSection === 'supply' ? 'supplier' : 'transport';
      this.router.navigate(['/cargo-owner/supplies', this.calculationId, tab], { replaceUrl: true });
    }
  }

  get pageTitle(): string {
    const title = this.calculation?.title || ('#' + this.calculationId);
    return this.titleText.replace('{title}', title);
  }

  private load(startIfNeeded: boolean): void {
    this.loading = true;
    this.error = null;

    this.subscriptions.add(
      this.customsCalculationsService.getCalculationById(this.calculationId).subscribe({
        next: (calculation) => {
          this.calculation = calculation;
          this.loading = false;
          this.cdr.markForCheck();

          if (startIfNeeded && calculation.status === 'CREATED' && this.isCargoOwner()) {
            // Обработку запускаем до перехода в раздел, иначе запрос оборвётся вместе со страницей
            this.startProcessing(() => this.redirectToSection());
          } else {
            this.redirectToSection();
          }

          const requests = calculation.cargoRequests || [];
          this.cargoRequests = requests;
          // По умолчанию все карточки развёрнуты — сворачивание доступно по клику.
          for (const r of requests) {
            if (!this.expandedRequestIds.has(r.id)) {
              this.expandedRequestIds.add(r.id);
            }
          }
        },
        error: (err) => {
          console.error('Error loading calculation:', err);
          this.error = this.errorLoadCalculationText;
          this.loading = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  /** Заявка, по которой уже принята ставка и создана сделка — если есть, показываем
   *  во вкладке «Перевозка» только её, остальные заявки-сёстры больше не нужны. */
  get dealRequest(): CargoRequest | undefined {
    return this.cargoRequests.find(r => r.dealId);
  }

  isRequestExpanded(requestId: number): boolean {
    return this.expandedRequestIds.has(requestId);
  }

  toggleRequestExpanded(requestId: number): void {
    if (this.expandedRequestIds.has(requestId)) {
      this.expandedRequestIds.delete(requestId);
    } else {
      this.expandedRequestIds.add(requestId);
    }
  }

  requestSummary(r: CargoRequest): string {
    return `${r.fromCity} → ${r.toCity}`;
  }

  isCargoOwner(): boolean {
    return this.authService.getUser()?.role?.code === 'ROLE_CARGO_OWNER';
  }

  isPublicUser(): boolean {
    return this.authService.getUser()?.role?.code === 'ROLE_PUBLIC';
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private startProcessing(then?: () => void): void {
    this.subscriptions.add(
      this.customsCalculationsService.startProcessing(this.calculationId).subscribe({
        next: (calculation) => {
          this.calculation = { ...calculation, cargoRequests: calculation.cargoRequests ?? this.calculation?.cargoRequests };
          this.cdr.markForCheck();
          then?.();
        },
        error: (err) => {
          console.error('Error starting processing:', err);
          then?.();
        }
      })
    );
  }

  onUpgraded(): void {
    this.showUpgradeModal = false;
    this.startProcessing(() => this.redirectToSection());
  }

  onUpgradeCancel(): void {
    this.showUpgradeModal = false;
    this.router.navigate(['/calculations', this.calculationId]);
  }

  switchTab(tab: 'calculation' | 'transportation' | 'supply'): void {
    this.activeTab = tab;
  }

  generateSupplierInvite(): void {
    if (this.generatingSupplierInvite || !this.calculation) return;
    this.generatingSupplierInvite = true;
    this.supplierInviteError = null;
    this.supplierInviteCopied = false;

    this.subscriptions.add(
      this.customsCalculationsService.generateSupplierInvite(this.calculationId).subscribe({
        next: ({ token }) => {
          this.supplierInviteLink = `${window.location.origin}/invite/supplier/${token}`;
          this.generatingSupplierInvite = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error generating supplier invite:', err);
          this.supplierInviteError = err.error?.message || this.errorGenerateInviteText;
          this.generatingSupplierInvite = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  copySupplierInviteLink(): void {
    if (!this.supplierInviteLink) return;
    navigator.clipboard?.writeText(this.supplierInviteLink).then(() => {
      this.supplierInviteCopied = true;
      this.cdr.markForCheck();
    });
  }

  /** Заявка была создана и привязана к расчёту — перезагружаем список заявок и прячем форму. */
  onRequestCreated(): void {
    this.showAddRequestForm = false;
    this.load(false);
  }

  onBack(): void {
    this.router.navigate(['/calculations', this.calculationId]);
  }
}
